/**
 * ai-wizard — Phase 25 Increment 11.
 *
 * Client for the Worker's /ai/* wizard endpoints (Cloudflare Worker →
 * FastAPI core → Claude). The backend is ALREADY deployed and live — the
 * audit's "Magic Wand has zero AI execution" was a pure frontend gap: the
 * MagicWandModal's onRunWizard callback reached no real code. This module
 * maps each wizard key (AI-01..AI-50) to its live `/api/youtube-shorts/ai/*`
 * endpoint and calls it.
 *
 * Endpoints that don't exist yet for a given wizard return
 * { ok:false, error:"not_implemented" } so the caller reports honestly.
 * All endpoints are auth-gated on the Worker — 401 means log in.
 */

/** Wizard key → Worker `/ai/<endpoint>` path. Only wizards with a live
 * backend endpoint are listed; the rest report "not_implemented". */
export const WIZARD_ENDPOINT: Record<string, string> = {
	"AI-02": "titles", // 제목 5개 추천
	"AI-03": "title-tones", // 제목 다양화
	"AI-04": "channel-name", // 채널명 매칭
	"AI-05": "voice-isolate", // 음원 분리 (Increment 16)
	"AI-06": "typo-correct", // 자막 오타 수정
	"AI-07": "word-emphasis", // 단어 강조 추천
	"AI-08": "translate-subtitles", // 자막 번역 한↔영 (Increment 19)
	"AI-09": "subtitle-styles", // 자막 스타일 추천
	"AI-10": "font-recommend", // 폰트 추천
	"AI-11": "color-palette", // 색상 팔레트
	"AI-12": "background-recommend", // 배경 추천
	"AI-13": "shape-recommend", // 도형 / 아이콘
	"AI-14": "image-search", // 이미지 자동 검색
	"AI-15": "image-generate", // AI 이미지 생성
	"AI-16": "bgm", // BGM 추천
	"AI-17": "bgm-beat-detect", // BGM 비트 검출
	"AI-19": "effect-recommend", // 필터 추천
	"AI-20": "transition-recommend", // 트랜지션 추천
	"AI-21": "magic-cut", // Magic Cut
	"AI-22": "clip-order", // 클립 순서 정렬
	"AI-23": "hashtags", // 해시태그 10개
	"AI-25": "thumbnail-text", // 썸네일 텍스트
	"AI-26": "category-classify", // 카테고리 분류
	"AI-27": "upload-meta", // 업로드 메타 작성
	"AI-28": "layout-recommend", // 레이아웃 추천
	"AI-29": "alpha-topic", // 알파토픽 트렌드
	"AI-30": "tts", // TTS 나레이션
	"AI-31": "speaker-diarize", // 화자 트래킹 (Increment 16)
	"AI-35": "auto-color-grade", // 색감 자동 보정
	"AI-39": "chapters", // 자동 챕터 마커 (Increment 16)
	"AI-44": "thumbnail-text", // A/B 썸네일 (Increment 21)
	"AI-46": "sweet-spot", // Retention 시뮬 (Increment 19)
	"AI-47": "upload-meta", // SEO description (Increment 21)
	"AI-49": "profanity-scan", // Quality Check (Increment 21)
	"AI-50": "personalize", // 편집 패턴 학습 (Increment 19)
	"AI-18": "bgm-duck", // BGM Ducking (Phase 25 engine)
	"AI-32": "bg-remove", // 배경 제거 MediaPipe (Phase 25 engine)
	"AI-33": "noise-reduce", // 노이즈 제거 (Phase 25 engine)
	"AI-34": "stabilize", // 흔들림 보정 vidstab (Phase 25 engine)
	"AI-36": "voice-change", // 음성 변조 6 프리셋 (Phase 25 engine)
	"AI-41": "loudness-normalize", // Loudness Normalize EBU R128 (Phase 25 engine)
	"AI-42": "slow-mo", // Slow-mo 자동 (Phase 25 engine)
	"AI-43": "upscale", // Upscale 4K Real-ESRGAN (Phase 25 engine)
	"AI-24": "thumbnails", // 6-shot thumbnail candidates (Phase 25 engine)
	"AI-48": "subtitle-line-break", // 의미 단위 자막 줄바꿈 (Phase 25, algorithmic free)
	"AI-37": "speed-ramp", // 영상 속도 가변 (Phase 25 engine, piecewise setpts+atempo)
};

export interface WizardRunResult {
	ok: boolean;
	status: number;
	endpoint?: string;
	data?: unknown;
	error?: string;
	balance?: number;
	required?: number;
}

/**
 * Run one Magic Wand wizard against its live backend endpoint.
 * `payload` is forwarded as the JSON body — the core endpoints all have
 * sensible fallbacks, so an empty `{}` still returns useful data.
 */
export async function runWizard(
	wizardKey: string,
	payload: Record<string, unknown> = {},
): Promise<WizardRunResult> {
	const endpoint = WIZARD_ENDPOINT[wizardKey];
	if (!endpoint) {
		return {
			ok: false,
			status: 0,
			error: "not_implemented",
		};
	}
	try {
		const r = await fetch(`/api/youtube-shorts/ai/${endpoint}`, {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify(payload),
		});
		const json = (await r.json().catch(() => ({}))) as {
			error?: string;
			balance?: number;
			required?: number;
			[k: string]: unknown;
		};
		if (!r.ok) {
			return {
				ok: false,
				status: r.status,
				endpoint,
				error: json.error ?? `wizard_failed_${r.status}`,
				balance: json.balance,
				required: json.required,
			};
		}
		return { ok: true, status: r.status, endpoint, data: json };
	} catch (e) {
		return { ok: false, status: 0, endpoint, error: String(e) };
	}
}

/* -------------------------------------------------------------------------
 * Phase 25 — engine wizards that process the editor's current video/audio.
 * These need a real server-side file path, so the Magic Wand modal must
 * upload the project media first (uploadMediaForWizard) and then call
 * runWizard with the returned path under the right payload key.
 * ---------------------------------------------------------------------- */

/** Wizard key → which payload field the uploaded media path goes into. */
export const ENGINE_WIZARD_INPUT: Record<string, "video_path" | "audio_path"> = {
	"AI-41": "video_path", // loudness-normalize
	"AI-34": "video_path", // stabilize
	"AI-42": "video_path", // slow-mo
	"AI-32": "video_path", // bg-remove
	"AI-43": "video_path", // upscale
	"AI-33": "audio_path", // noise-reduce
	"AI-36": "audio_path", // voice-change
	// AI-18 (bgm-duck) needs two inputs — handled specially by the caller.
};

/** True when a wizard needs the editor's current media uploaded first. */
export function isEngineWizard(wizardKey: string): boolean {
	return wizardKey in ENGINE_WIZARD_INPUT || wizardKey === "AI-18";
}

export interface UploadMediaResult {
	ok: boolean;
	path?: string;
	filename?: string;
	bytes?: number;
	status: number;
	error?: string;
}

/**
 * Upload one editor media File to the server so an engine wizard can read it.
 * Returns the server-side absolute path on success.
 */
export async function uploadMediaForWizard(
	file: File,
): Promise<UploadMediaResult> {
	try {
		const fd = new FormData();
		fd.append("media", file, file.name || "media.bin");
		const r = await fetch("/api/youtube-shorts/ai/upload-media", {
			method: "POST",
			body: fd,
		});
		const json = (await r.json().catch(() => ({}))) as {
			path?: string;
			filename?: string;
			bytes?: number;
			error?: string;
		};
		if (!r.ok || json.error) {
			return {
				ok: false,
				status: r.status,
				error: json.error ?? `upload_failed_${r.status}`,
			};
		}
		return {
			ok: true,
			status: r.status,
			path: json.path,
			filename: json.filename,
			bytes: json.bytes,
		};
	} catch (e) {
		return { ok: false, status: 0, error: String(e) };
	}
}

/**
 * Full engine-wizard flow: upload `file`, then run the wizard with the
 * server path threaded into the correct payload field. For AI-18 (bgm-duck)
 * pass `bgmFile` too — it is uploaded as the second input.
 */
export async function runEngineWizard(
	wizardKey: string,
	file: File,
	opts: { bgmFile?: File; extra?: Record<string, unknown> } = {},
): Promise<WizardRunResult> {
	const up = await uploadMediaForWizard(file);
	if (!up.ok || !up.path) {
		return {
			ok: false,
			status: up.status,
			error: up.error ?? "upload_failed",
		};
	}
	const payload: Record<string, unknown> = { ...(opts.extra ?? {}) };

	if (wizardKey === "AI-18") {
		// bgm-duck — voice + bgm. Without a separate BGM track we cannot run.
		if (!opts.bgmFile) {
			return {
				ok: false,
				status: 0,
				error: "bgm_track_required",
			};
		}
		const bgmUp = await uploadMediaForWizard(opts.bgmFile);
		if (!bgmUp.ok || !bgmUp.path) {
			return {
				ok: false,
				status: bgmUp.status,
				error: bgmUp.error ?? "bgm_upload_failed",
			};
		}
		payload.voice_path = up.path;
		payload.bgm_path = bgmUp.path;
	} else {
		const field = ENGINE_WIZARD_INPUT[wizardKey] ?? "video_path";
		payload[field] = up.path;
	}
	return runWizard(wizardKey, payload);
}

/**
 * Build a same-origin download URL for an engine-wizard output file
 * (server-side path). Used by the Magic Wand modal to offer the processed
 * video/audio as a download.
 */
export function wizardOutputUrl(serverPath: string): string {
	return `/api/youtube-shorts/ai/wizard-output?path=${encodeURIComponent(serverPath)}`;
}

/** Pull the `output_path` field out of a wizard run result, if present. */
export function outputPathFromResult(res: WizardRunResult): string | null {
	if (!res.ok || !res.data || typeof res.data !== "object") return null;
	const op = (res.data as { output_path?: unknown }).output_path;
	return typeof op === "string" && op.length > 0 ? op : null;
}
